import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { InviteService, type UserInvite } from "@/services/inviteService";
import { RBACService } from "@/services/rbacService";
import { toast } from "react-hot-toast";

interface InviteFormData {
  email: string;
  firstName: string;
  lastName: string;
  roleId: string;
}

// Export query key for prefetching
export const INVITES_QUERY_KEY = ["invites"];

export function InviteUserManagement() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<InviteFormData>({
    email: "",
    firstName: "",
    lastName: "",
    roleId: "",
  });
  const [sending, setSending] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Fetch invites using react-query for caching and prefetching
  const {
    data: invitesData,
    isLoading: loading,
    error: invitesError,
  } = useQuery({
    queryKey: INVITES_QUERY_KEY,
    queryFn: async () => {
      const result = await InviteService.getInvites();
      if (result.error) {
        throw new Error(result.error);
      }
      return result.invites || [];
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  const invites: UserInvite[] = invitesData || [];
  const error = invitesError ? (invitesError as Error).message : "";

  // Fetch available roles from RBAC system
  const { data: availableRoles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: RBACService.getRoles,
  });

  const refetchInvites = () => {
    queryClient.invalidateQueries({ queryKey: INVITES_QUERY_KEY });
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      toast.error("Email is required");
      return;
    }

    if (!formData.roleId) {
      toast.error("Please select a role");
      return;
    }

    // Get the role name from the selected role ID
    const selectedRole = availableRoles.find((r) => r.id === formData.roleId);

    try {
      setSending(true);
      const result = await InviteService.sendInvite({
        email: formData.email,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        role: selectedRole?.name || "user",
        roleId: formData.roleId,
      });

      if (result.success) {
        toast.success(result.message || "Invitation sent successfully!");
        setShowModal(false);
        setFormData({ email: "", firstName: "", lastName: "", roleId: "" });
        refetchInvites();
      } else {
        toast.error(result.error || "Failed to send invitation");
      }
    } catch {
      toast.error("Failed to send invitation");
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (inviteId: string) => {
    try {
      setProcessingId(inviteId);
      const result = await InviteService.resendInvite(inviteId);
      if (result.success) {
        toast.success("Invitation resent successfully!");
        refetchInvites();
      } else {
        toast.error(result.error || "Failed to resend invitation");
      }
    } catch {
      toast.error("Failed to resend invitation");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (inviteId: string) => {
    if (!confirm("Are you sure you want to cancel this invitation?")) {
      return;
    }

    try {
      setProcessingId(inviteId);
      const result = await InviteService.deleteInvite(inviteId);
      if (result.success) {
        toast.success("Invitation cancelled");
        refetchInvites();
      } else {
        toast.error(result.error || "Failed to cancel invitation");
      }
    } catch {
      toast.error("Failed to cancel invitation");
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-3 py-1 rounded-full text-[12px] font-semibold";
    switch (status) {
      case "pending":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case "accepted":
        return `${baseClasses} bg-green-100 text-green-800`;
      case "expired":
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case "cancelled":
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-start w-full overflow-y-auto min-h-[300px] md:min-h-[400px] max-h-[calc(100vh-250px)] md:max-h-[calc(100vh-350px)] p-4 rounded-[10px] border border-gray-100">
        <div className="flex items-center justify-center w-full h-full">
          <div className="text-[#828282] text-[14px]">
            Loading invitations...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start w-full overflow-y-auto min-h-[300px] md:min-h-[400px] max-h-[calc(100vh-250px)] md:max-h-[calc(100vh-350px)] p-4 rounded-[10px] border border-gray-100">
      <div className="w-full">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-[#444444] text-[18px] font-bold">
              User Invitations
            </h1>
            <p className="text-[#828282] text-[13px] mt-1">
              Invite new users to the system
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="bg-[#374795] text-white rounded-[10px] px-4 py-2.5 text-[13px] font-semibold hover:bg-[#132477] transition-colors whitespace-nowrap flex-shrink-0"
          >
            + Invite User
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-[10px]">
            {error}
          </div>
        )}

        {invites.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-[#828282] text-[14px]">
              No invitations sent yet. Click "Invite User" to get started.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {invites.map((invite) => (
              <div
                key={invite.id}
                className="bg-white rounded-2xl p-4 border border-gray-100"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-[#444444] text-[16px] font-semibold">
                        {invite.first_name || invite.last_name
                          ? `${invite.first_name || ""} ${invite.last_name || ""}`.trim()
                          : invite.email}
                      </h3>
                      <span className={getStatusBadge(invite.status)}>
                        {invite.status.charAt(0).toUpperCase() +
                          invite.status.slice(1)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-[#828282] text-[12px] font-semibold uppercase mb-1">
                          Email
                        </p>
                        <p className="text-[#444444] text-[14px]">
                          {invite.email}
                        </p>
                      </div>

                      <div>
                        <p className="text-[#828282] text-[12px] font-semibold uppercase mb-1">
                          Role
                        </p>
                        <p className="text-[#444444] text-[14px] capitalize">
                          {invite.role}
                        </p>
                      </div>

                      <div>
                        <p className="text-[#828282] text-[12px] font-semibold uppercase mb-1">
                          Invited
                        </p>
                        <p className="text-[#444444] text-[14px]">
                          {formatDate(invite.invited_at)}
                        </p>
                      </div>
                    </div>

                    {invite.accepted_at && (
                      <div className="mb-4">
                        <p className="text-[#828282] text-[12px] font-semibold uppercase mb-1">
                          Accepted
                        </p>
                        <p className="text-[#444444] text-[14px]">
                          {formatDate(invite.accepted_at)}
                        </p>
                      </div>
                    )}

                    {invite.inviter && (
                      <div className="text-[#828282] text-[12px]">
                        Invited by: {invite.inviter.first_name}{" "}
                        {invite.inviter.last_name} ({invite.inviter.email})
                      </div>
                    )}
                  </div>
                </div>

                {invite.status === "pending" && (
                  <div className="pt-4 border-t border-gray-200 flex gap-3">
                    <button
                      onClick={() => handleResend(invite.id)}
                      disabled={processingId === invite.id}
                      className="border border-[#374795] text-[#374795] rounded-[10px] px-4 py-2 text-[14px] font-semibold hover:bg-[#374795] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {processingId === invite.id ? "Sending..." : "Resend"}
                    </button>

                    <button
                      onClick={() => handleDelete(invite.id)}
                      disabled={processingId === invite.id}
                      className="border border-red-500 text-red-500 rounded-[10px] px-4 py-2 text-[14px] font-semibold hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {processingId === invite.id ? "..." : "Cancel"}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <h2 className="text-[#444444] text-[20px] font-bold mb-4">
              Invite New User
            </h2>

            <form onSubmit={handleSendInvite} className="space-y-4">
              <div>
                <label className="text-[#828282] text-[12px] font-semibold uppercase mb-2 block">
                  Email *
                </label>
                <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="user@example.com"
                    required
                    className="w-full bg-transparent text-[#444444] text-[14px] placeholder:text-[#828282] border-none outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[#828282] text-[12px] font-semibold uppercase mb-2 block">
                    First Name
                  </label>
                  <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) =>
                        setFormData({ ...formData, firstName: e.target.value })
                      }
                      placeholder="John"
                      className="w-full bg-transparent text-[#444444] text-[14px] placeholder:text-[#828282] border-none outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[#828282] text-[12px] font-semibold uppercase mb-2 block">
                    Last Name
                  </label>
                  <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) =>
                        setFormData({ ...formData, lastName: e.target.value })
                      }
                      placeholder="Doe"
                      className="w-full bg-transparent text-[#444444] text-[14px] placeholder:text-[#828282] border-none outline-none"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[#828282] text-[12px] font-semibold uppercase mb-2 block">
                  Role *
                </label>
                <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                  <select
                    value={formData.roleId}
                    onChange={(e) =>
                      setFormData({ ...formData, roleId: e.target.value })
                    }
                    required
                    className="w-full bg-transparent text-[#444444] text-[14px] border-none outline-none"
                  >
                    <option value="">
                      {rolesLoading ? "Loading roles..." : "Select a role"}
                    </option>
                    {availableRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                        {role.description ? ` - ${role.description}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 rounded-[10px] px-6 py-3 text-[14px] font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="flex-1 bg-[#374795] text-white rounded-[10px] px-6 py-3 text-[14px] font-semibold hover:bg-[#132477] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {sending ? "Sending..." : "Send Invitation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
